/**
 * AŞAMA 2 SÖZLEŞMESİ — merkezi fiyat arşivinin dış yüzü.
 *
 * KURAL: Bu dosya Aşama 2 boyunca değişmez. A şeridi (toplayıcılar) ve
 * B şeridi (merkez + kuyruk + API) bunu UYGULAR, C şeridi (cihaz önbelleği)
 * ve E şeridi (arama akışı) bunu ÇAĞIRIR.
 *
 * TASARIM İLKESİ — talep üzerine izleme:
 * Merkez tüm piyasayı önceden indirmez. Bir araç ancak birisi ona ilk
 * hareketi girdiğinde izlemeye girer. Katalog (ne var) ile arşiv (fiyatı
 * tutulan) AYRI: katalog geniş ve ucuz, arşiv dar ve pahalı.
 *
 * MAHREMİYET — pazarlığa kapalı:
 * Hiçbir uç nokta kimlik doğrulaması istemez ve `tracked_instruments`
 * tablosunda KULLANICI KOLONU YOKTUR. `/track` çağrısı ne kimlik ne tarih
 * taşır. Sunucu neyin izlendiğini bilir, kimin istediğini bilmez.
 */

import type {
  AssetClass,
  Coverage,
  CurrencyCode,
  DailyBar,
  Decimal,
  FxRate,
  InstrumentId,
  IsoDate,
  IsoTimestamp,
  SourceLimits,
  TrackState,
} from "./index.ts";

// ---------------------------------------------------------------------------
// AŞAMA 0'DA ÖLÇÜLEN LİMİTLER — kuyruk bütçesi bunlara dayanır
// ---------------------------------------------------------------------------

/**
 * Kullanıcının makinesinde 04.09.2026'da, 211 canlı HTTP isteğiyle ölçüldü.
 * Bu sayılar VARSAYIM DEĞİL; `tools/verify-sources` koşumunun çıktısıdır.
 * Yeniden ölçülmeden değiştirilmemeli.
 *
 * "YENİDEN ÖLÇÜM" İKİ BİÇİMDE OLABİLİR ve ikincisi Aşama 0'da yoktu: bir
 * probun PATLAMA hızı ile kuyruğun bindirdiği SÜREKLİ YÜK aynı sınırı
 * görmüyor. `tefas` bunun kaydı — patlamada 140 istek/dk sorunsuzdu, canlı
 * işçi sürekli yükte ~21 istekte HTTP 429 aldı. Böyle bir çakışmada eski sayı
 * SİLİNMEZ; ikisi de yazılır ve kuyruğun kullandığı sayı, kuyruğun gerçekten
 * ürettiği yük biçimine ait olandır (bkz. aşağıdaki `tefas` notu).
 */
export const MEASURED_LIMITS: Readonly<Record<string, SourceLimits>> = {
  tcmb: {
    reqPerMin: 60,
    maxDaysPerCall: 1, // YAPISAL: günde bir dosya, tarih aralığı parametresi yok
    maxSymbolsPerCall: 1, // tek dosya o günün TÜM kurlarını taşır
    requiresKey: false,
    provenance: {
      reqPerMin: "measured",
      maxDaysPerCall: "structural",
      maxSymbolsPerCall: "structural",
      requiresKey: "structural",
    },
  },
  frankfurter: {
    reqPerMin: 60,
    maxDaysPerCall: 7300, // ÖLÇÜLDÜ: 20 yıl TEK çağrıda döndü
    maxSymbolsPerCall: 30,
    requiresKey: false,
    // `maxHistoryYears` YOK ve bu bilinçli: 20 yıl tek çağrıda döndüğü için
    // bu kaynakta bilinen bir taban gözlenmedi. Ölçülmemiş bir sınırı
    // yazmak, olmayan bir kısıtı olgu gibi göstermek olurdu.
    provenance: { reqPerMin: "measured", maxDaysPerCall: "measured", maxSymbolsPerCall: "assumed" },
  },
  yahoo: {
    reqPerMin: 60, // gözlenen 905/dk; ihtiyati kırpıldı
    maxDaysPerCall: 3650, // ÖLÇÜLDÜ: 10 yıl TEK çağrıda, kırpma yok
    maxSymbolsPerCall: 1,
    requiresKey: false,
    provenance: { reqPerMin: "measured", maxDaysPerCall: "measured", maxSymbolsPerCall: "structural" },
  },
  binance: {
    reqPerMin: 177,
    maxDaysPerCall: 999,
    rowCap: 1000, // sınır gün değil KAYIT sayısı — 1825 günde sessiz kırpma görüldü
    maxSymbolsPerCall: 1,
    requiresKey: false,
    provenance: { reqPerMin: "measured", maxDaysPerCall: "measured", maxSymbolsPerCall: "structural" },
  },
  /**
   * TEFAS — İKİ ÖLÇÜM VAR VE İKİSİ DE DOĞRU; KULLANILAN SÜREKLİ YÜK ÖLÇÜMÜ.
   *
   * 1) PATLAMA ÖLÇÜMÜ (04.09.2026, Aşama 0, `tools/verify-sources`): 140
   *    istek/dk. Kısa bir patlamada gerçekten bu hıza çıkıldı ve 429
   *    görülmedi. Sayı SİLİNMİYOR — yapılan ölçüm bir olgudur.
   * 2) SÜREKLİ YÜK ÖLÇÜMÜ (kullanıcının VPS'i, canlı işçi koşumu): kuyruk 122
   *    geçmiş işini sıraya aldı, kova `remaining: 119` gösterdi (yani bir
   *    dakikalık pencerede ~21 istek harcanmış) ve TEFAS
   *    `HTTP 429 · /api/funds/fonGnlBlgSiraliGetirDosya` döndürdü. Yani
   *    SÜREKLİ yükte gerçek eşik ~21 istek/dk mertebesinde, 140'ın çok altında.
   *
   * NEDEN 12: patlama ölçümü kuyruk için YANLIŞ SORUNUN cevabıydı — kuyruk
   * dakikalarca kesintisiz yük bindiriyor, birkaç saniyelik patlama değil.
   * 12, gözlenen 429 eşiğinin (~21) yaklaşık %57'si; 429 tekrar görülürse
   * suçlu limitin kendisi olmasın diye ihtiyati paya yer bırakıyor. Bedeli
   * kabul edilebilir: TEFAS geri doldurması bu kaynağın ulaşabildiği 5 yıl
   * için ~66 çağrı (fonKod=null TÜM fonları getirdiği için 1500 fonun tamamı
   * o çağrılarda), 12/dk'da ~6 dakika. (Bu sayı 122'ydi; `maxHistoryYears`
   * ölçüldükten sonra yarıya indi — 10 yıllık ufkun ikinci yarısı için açılan
   * ~61 iş zaten HİÇ başarılı olamıyordu. Sonra `maxDaysPerCall` 30'dan 28'e
   * indi ve ~61 → ~66 oldu.) TEK SEFERLİK bir iş için bu bir
   * maliyet değil; buna karşılık
   * 429 yüzünden aracın `unresolved` damgalanması kullanıcıya doğrudan
   * görünen bir yanlış teşhistir.
   *
   * NEDEN "ölçülmeden değiştirilmez" KURALI ÇİĞNENMİYOR: yeni sayı bir
   * varsayım değil, YENİ BİR ÖLÇÜMÜN muhafazakâr yorumu. Ölçüm koşulu
   * (patlama → sürekli yük) değiştiği için sayı da değişti; ikisi de yukarıda
   * kayıtlı, `provenance` de bunu söylüyor.
   */
  tefas: {
    reqPerMin: 12,
    /**
     * 28, 30 DEĞİL — VE FARK CANLI ÜRETİMDE ORTAYA ÇIKTI (08.09.2026).
     *
     * TEFAS'ın sınırı GÜN DEĞİL, TAKVİM AYI:
     *   "Geçersiz veri: Tarih aralığı 1 ayı aşamaz"
     *
     * 30 günlük (kapalı aralık: `from` + 29 gün) bir pencere, ŞUBAT'ta
     * başladığında bu sınırı aşıyor — çünkü şubat 28/29 günlük. Kanıt: canlı
     * kuyrukta düşen dört pencere ve düşmeyen beşincisi:
     *   2022-02-02 → 2022-03-03  ✗ (1 ay siniri 2022-03-02)
     *   2023-02-27 → 2023-03-28  ✗
     *   2025-02-16 → 2025-03-17  ✗
     *   2026-02-11 → 2026-03-12  ✗
     *   2024-02-22 → 2024-03-22  ✓ ARTIK YIL: tam 1 ay, kabul edildi
     * Yani yılda bir pencere (artık yıllar hariç) sessizce ölüyordu ve zincir
     * o noktada kopuyordu — su işareti daha geriye gidemiyordu.
     *
     * NEDEN 28 HER ZAMAN GÜVENLİ: her takvim ayının EN AZ 28 günü var, yani
     * `from + 27 gün ≤ from + 1 ay` her tarih için doğru. 2020-2030 aralığının
     * her günü için sınandı; en dar pay 1 gün (31 Ocak'ta başlayan pencere).
     * "1 ayı aşamaz" ifadesinin `≤` olduğu da yukarıdaki artık yıl satırıyla
     * ölçülü: tam 1 ay kabul ediliyor.
     *
     * BEDELİ: 5 yıl için parça sayısı ~61 → ~66. Beş fazladan çağrı, yılda bir
     * kopan zincire karşı ucuz.
     *
     * ALTERNATİF (SEÇİLMEDİ): parçalamayı takvim ayına çevirmek daha "doğru"
     * bir modelleme olurdu ama `chunkRangeBackward` sabit parça boyuna dayanıyor
     * ve `source_wide` işlerin ızgara hizalaması (`alignGrid`) da öyle — değişken
     * boy, aynı aralığın iki farklı anahtarla iki kez çekilmesine yol açardı.
     */
    maxDaysPerCall: 28,
    // ÖLÇÜLDÜ — KAYNAĞIN KENDİ REDDİ, canlı koşumda (08.09.2026):
    // 2019-2020 aralıkları için HTTP 200 + gövdede
    //   "Geçersiz veri: Baslangıc Tarihi 5 yıldan eski olamaz"
    // Yani TEFAS arşivi 5 yıl derinliğinde. 10 yıllık ufuk bu kaynakta
    // karşılanamaz ve karşılanamadığı SÖYLENMEK zorunda (bkz. SourceLimits).
    maxHistoryYears: 5,
    maxSymbolsPerCall: 1, // fonKod=null ⇒ TÜM fonlar; aşağıdaki nota bak
    requiresKey: false,
    // `reqPerMin` HÂLÂ "measured": dayanağı canlı bir 429 gözlemi. Patlama
    // ölçümündeki 140 ile karıştırılmaması için gerekçe yukarıda tam yazılı —
    // bir sonraki okuyan "niçin 140 değil" sorusunun cevabını orada bulur.
    provenance: {
      reqPerMin: "measured",
      maxDaysPerCall: "measured",
      // "measured": dayanağı kaynağın kendi hata mesajı — bir varsayım değil,
      // canlı koşumda okunan bir ret.
      maxHistoryYears: "measured",
      maxSymbolsPerCall: "assumed",
    },
  },
  coingecko: {
    reqPerMin: 10, // ölçülemedi (hız probu 429 aldı) — ihtiyati varsayım
    maxDaysPerCall: 365, // ücretsiz katman sınırı, 400 günde HTTP 401
    maxSymbolsPerCall: 1,
    requiresKey: false,
    provenance: { reqPerMin: "assumed", maxDaysPerCall: "measured" },
  },
};

/**
 * TEFAS'ın özel durumu: `fonKod` boş bırakıldığında aralıktaki TÜM fonlar
 * tek yanıtta gelir. Yani geri doldurma bütçesi ARAÇ BAŞINA değil TARİH
 * ARALIĞI BAŞINA hesaplanır: kaynağın verdiği 5 yıl ≈ 66 çağrı (28 günlük
 * parçalarla), ~1500 fonun TAMAMI için. (10 yıl 122 çağrı olurdu ama TEFAS
 * 5 yıldan eskisini vermiyor — bkz. `maxHistoryYears`.)
 * Kuyruk bunu bilmezse 1500 × 66 iş açar ve TEFAS'ı gereksiz yorar.
 */
export const TEFAS_FETCHES_ALL_FUNDS_AT_ONCE = true;

/**
 * Varlık sınıfına göre geçmiş ufku. Aşama 0'da Binance "10 yıl karşılanmıyor"
 * damgası aldı, ama sebep kaynağın kusuru değil BTCUSDT'nin 2017'de
 * başlamasıydı. Ufuk sabit bir sayı değil, ihraç tarihiyle sınırlı bir hedef.
 */
export function horizonFor(
  assetClass: AssetClass,
  today: IsoDate,
  inception: IsoDate | null,
): IsoDate {
  const tenYearsAgo = shiftYears(today, -10);
  if (inception === null) return tenYearsAgo;
  return inception > tenYearsAgo ? inception : tenYearsAgo;
}

/**
 * Yıl kaydırma — ayın son gününe kırparak.
 *
 * İLK SÜRÜM HATALIYDI ve B şeridi ölçerek buldu: yılı string yapıştırmayla
 * değiştiriyordu, yani `today = "2028-02-29"` için `"2018-02-29"` üretiyordu.
 * Öyle bir gün yok; Postgres DATE'e çevirmiyor ve `/track` dört yılda bir o
 * gün patlıyordu. Artık 28'e kırpılıyor.
 */
export function shiftYears(date: IsoDate, years: number): IsoDate {
  const y = Number.parseInt(date.slice(0, 4), 10) + years;
  const m = Number.parseInt(date.slice(5, 7), 10);
  const d = Number.parseInt(date.slice(8, 10), 10);
  const last = daysInMonth(y, m);
  const day = d > last ? last : d;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [1, 3, 5, 7, 8, 10, 12].includes(month) ? 31 : 30;
}

// ---------------------------------------------------------------------------
// /resolve — katalogdan aday araçlar
// ---------------------------------------------------------------------------

export interface ResolveRequest {
  /** Serbest metin: "türk hava", "THYAO", "BTC", bir ISIN. */
  q: string;
  assetClasses?: readonly AssetClass[];
  limit?: number;
}

export interface ResolveResponse {
  candidates: readonly ResolveCandidateDto[];
  /** Katalog en son ne zaman yenilendi — kullanıcıya tazelik bilgisi. */
  catalogSyncedAt: IsoTimestamp | null;
  /**
   * Dış aramanın (Yahoo) bu sorguda ne yaptığı. "BULUNAMADI" İLE "ŞU AN
   * SORULAMADI" AYRI ŞEYLER: `butce-yok`/`hata` iken boş liste "böyle bir araç
   * yok" DEMEK DEĞİL. Aşama 2'de sözleşme donuk olduğu için yalnızca
   * `X-Parafon-Resolve-Remote` başlığındaydı ve cihaz onu hiç okumuyordu;
   * ekran her durumda "Eşleşen araç bulunamadı." diyordu (27.09.2026).
   * İsteğe bağlı: eski merkez göndermez.
   */
  remoteLookup?: RemoteLookup;
}

/** Dış arama sonucu — değer bir DURUM ADI; sorgu ya da hata metni taşımaz. */
export type RemoteLookup =
  | "kapali" // dış arama kurulmamış
  | "gerekmedi" // yerel katalog kodu tam tanıdı
  | "kisa-sorgu"
  | "onbellek" // aynı sorgu az önce soruldu
  | "butce-yok" // hız kovası boş — SORULAMADI
  | "yazildi" // soruldu, aday(lar) kataloğa yazıldı
  | "bos" // soruldu, uyan aday yok
  | "hata"; // soruldu, kaynak patladı — SORULAMADI

export interface ResolveCandidateDto {
  instrumentId: InstrumentId;
  symbol: string;
  exchange: string;
  assetClass: AssetClass;
  currency: CurrencyCode;
  name: string;
  isin?: string;
  /** 0..1 arası eşleşme güveni. Belirsizse kullanıcı seçer. */
  score: number;
  /** Bu araç şu an izleniyor mu ve verisi ne durumda. */
  trackState: TrackState | null;
}

// ---------------------------------------------------------------------------
// /track — izlemeye alma
// ---------------------------------------------------------------------------

/**
 * BİLİNÇLİ OLARAK boş: ne kullanıcı kimliği ne tarih taşır.
 * Tarih taşımaması bir mahremiyet kararıydı (kullanıcı "sabit 10 yıl ufku"
 * seçti), kimlik taşımaması ise hem daha az iş hem daha mahrem.
 * Fikirdeş: aynı aracı ikinci kez istemek zararsız.
 */
export interface TrackRequestDto {
  instrumentId: InstrumentId;
}

/**
 * UFKU FİİLEN BELİRLEYEN ŞEY — ÜÇÜ DE MEŞRU, AMA ANLAMLARI FARKLI.
 *
 * Bu tip bir dönem yalnızca merkezin iç katmanındaydı (`domain/horizon.ts`) ve
 * dışarı çıkmıyordu; arayüz ufkun NİÇİN kısa olduğunu söyleyemiyordu.
 * Aşama 3'ün ihtiyacı bu ayrım üzerine kurulu (plan madde 13):
 *
 *  - `target`    → 10 yıllık hedefe ulaşıldı. İstenen buydu.
 *  - `inception` → araç o tarihte ihraç edildi. Öncesinde veri YOK ve bu bir
 *                  EKSİKLİK DEĞİL; hesaplanacak bir şey yoktu.
 *  - `source`    → kaynağın arşivi o kadar derin. Fiyatlar piyasada VARDI,
 *                  bizde yok. Bu GERÇEK bir eksiklik.
 *
 * İkisini aynı saymak Aşama 3'te sessiz bir yalan üretirdi: bir fonun 8 yıl
 * önceki maliyeti "hesaplanamaz" ile "hiç var olmadı" arasındaki fark, vergi
 * hesabının doğru olup olmadığını belirliyor.
 */
export type HorizonLimit = "target" | "inception" | "source";

/**
 * Aynı kümenin ÇALIŞMA ZAMANI hâli. Tip yalnızca derleyiciye görünür; gelen
 * bir DTO'yu ya da veritabanı satırını doğrulamak için değere ihtiyaç var ve
 * o değerin iki yerde yazılması ayrışma riski demek.
 */
export const HORIZON_LIMITS: readonly HorizonLimit[] = ["target", "inception", "source"];

export interface TrackStatusDto {
  instrumentId: InstrumentId;
  state: TrackState;
  reason: "demand" | "seed" | "benchmark";
  /** Hedef: bugün − 10 yıl ya da ihraç tarihi (hangisi sonraysa). */
  horizonFrom: IsoDate;
  /** Ufku kim sınırladı — `horizonFrom` tek başına bunu söylemiyor. */
  horizonLimitedBy: HorizonLimit;
  /** Su işareti: geçmiş buraya kadar tam. null = henüz hiç geçmiş yok. */
  backfilledTo: IsoDate | null;
  /** Kaç iş kaldı ve tahmini bitiş — arayüz ilerlemeyi bundan gösterir. */
  pendingJobs: number;
  etaSeconds: number | null;
  lastError?: string;
}

// ---------------------------------------------------------------------------
// /tracked — izlenen araçların listesi (Dönemsel Analiz, 13.10.2026)
// ---------------------------------------------------------------------------

/**
 * MERKEZİN İZLEDİĞİ HER ARAÇ — künye ve veri durumu. Dönemsel Analiz
 * ekranının seçim listesi.
 *
 * MAHREMİYET KARARI (kullanıcı, 13.10.2026): liste herkese açıktır ve
 * kullanıcıların TOPLUCA neyi izlediğini söyler. Kabul edildi: kullanıcı az
 * iken yabancı da az; çoğalınca kimin neyi tuttuğu belirsizleşir. Liste yine
 * de KİMLİK TAŞIMAZ (tabloda yok) ve sayı, zaman, istek sıklığı gibi bir
 * aracın kaç kişide olduğunu ima edecek hiçbir alan dışarı çıkmaz.
 *
 * İstek GÖVDESİZ (`GET /tracked`): cihaz hiçbir şey söylemez. Listelemek
 * `last_query_at`i TAZELEMEZ — yoksa listeyi açmak her aracı uykudan
 * uyandırırdı.
 *
 * `unresolved` araçlar listede YOK: verisi olmayan bir aracı seçtirmek
 * boşuna. `dormant` olanlar VAR: geçmişleri silinene kadar yerinde.
 */
export interface TrackedListItemDto {
  instrumentId: InstrumentId;
  symbol: string;
  exchange: string;
  assetClass: AssetClass;
  currency: CurrencyCode;
  name: string;
  state: TrackState;
  horizonFrom: IsoDate;
  horizonLimitedBy: HorizonLimit;
  /** Geçmiş buraya kadar tam; null = henüz hiç geçmiş yok. */
  backfilledTo: IsoDate | null;
  /**
   * Merkezin kendiliğinden izlediği KIYAS aracı mı (BIST100, altın…;
   * `reason = 'benchmark'`). Kullanıcıyı ima etmez. Eski merkez göndermez.
   */
  kiyas?: boolean;
}

export interface TrackedListResponse {
  /** Sembole göre sıralı. */
  items: readonly TrackedListItemDto[];
}

// ---------------------------------------------------------------------------
// /prices — barlar + KAPSAM
// ---------------------------------------------------------------------------

export interface PricesRequest {
  ids: readonly InstrumentId[];
  from: IsoDate;
  to: IsoDate;
}

/**
 * KAPSAM BOŞLUĞUNDA DÜRÜSTLÜK KURALI — bu sözleşmenin en önemli maddesi.
 *
 * Yanıt yalnızca barları değil, araç başına KAPSAM aralığını da döndürür.
 * Analiz motoru kapsamı okumadan hesap yapmaz; kapsam dışı bir dönem için
 * sayı değil "eksik kapsam" döner.
 *
 * Sebep: geri doldurma sürerken üretilen bir rapor, veri bulunmayan dönemi
 * sıfır getiri gibi gösterirse fark edilmez ama YANLIŞTIR. Bu, risk
 * tablosundaki "sessiz hesap hatası"nın en olası tetikleyicisi.
 */
export interface PricesResponse {
  series: readonly {
    instrumentId: InstrumentId;
    currency: CurrencyCode;
    bars: readonly DailyBar[];
    coverage: Coverage;
  }[];
}

// ---------------------------------------------------------------------------
// /fx ve /benchmarks — talep beklemeyen muaf küme
// ---------------------------------------------------------------------------

export interface FxRequest {
  pairs: readonly (readonly [CurrencyCode, CurrencyCode])[];
  from: IsoDate;
  to: IsoDate;
}

/**
 * PARİTE BAŞINA KAPSAM — Aşama 3 planı madde 1.
 *
 * `/prices` her seri için kapsam döndürüyor ("kapsam boşluğunda dürüstlük
 * kuralı"), `/fx` ise YALNIZCA satırları döndürüyordu. Oysa TRY değerlemesi
 * kur satırlarına araç fiyatları kadar bağımlı: kurun eksik olduğu bir dönem
 * için üretilen "değer" sessizce yanlıştır. İki uçtan birinde kapsam
 * taşımamak, o dürüstlük kuralını yarım uygulamak olurdu.
 *
 * `SeriesCoverage`ı MİRAS ALIYOR: kıyas serilerinde kullanılan tip aynı soruya
 * cevap veriyor (from/to/gaps) ve kurun izleme durumu yok — araç şeklindeki
 * `Coverage` buraya uymuyor (aynı gerekçe `BenchmarkResponse`ta yazılı).
 */
export interface FxPairCoverage extends SeriesCoverage {
  readonly base: CurrencyCode;
  readonly quote: CurrencyCode;
}

export interface FxResponse {
  rates: readonly FxRate[];
  /**
   * Parite başına kapsam: elimizdeki gerçek zarf + istenen aralıktaki bilinen
   * boşluklar. `from`/`to` null = o parite için hiç satır yok.
   */
  coverage: readonly FxPairCoverage[];
  /**
   * İki kaynaklı tasarımın görünür yüzü: hangi satır nereden geldi.
   * Toplu geçmiş Frankfurter'dan (7300 gün/çağrı), günlük ve hukuken
   * bağlayıcı kur TCMB'den (günde 1 dosya). Kullanıcı kararı: kanonik kur
   * TCMB DÖVİZ ALIŞ (`ForexBuying`).
   */
  bySource: Readonly<Record<string, number>>;
  /** Mutabakat işinin işaretlediği sapmalar — eşik %2. */
  reconciliationFlags: readonly {
    date: IsoDate;
    base: CurrencyCode;
    quote: CurrencyCode;
    tcmbRate: Decimal;
    /** Frankfurter (ECB referans kurlarının vekili) — ölçülen kaynak bu. */
    frankfurterRate: Decimal;
    relativeDiff: number;
  }[];
}

/** Kanonik kur seçimi — kullanıcı kararı, tüm geçmiş değerleme buna dayanır. */
export const CANONICAL_FX_FIELD = "ForexBuying" as const;

/** Mutabakat eşiği: bunu aşan sapma `data_quality_flags`'e yazılır. */
export const FX_RECONCILIATION_THRESHOLD = 0.02;

export interface BenchmarkRequest {
  codes: readonly string[];
  from: IsoDate;
  to: IsoDate;
}

/**
 * Kıyas serileri talep beklemez, baştan ve tam tutulur: kullanıcı SAHİP
 * OLMADIĞI bir şeye karşı performans ölçmek isteyecek, dolayısıyla talep hiç
 * gelmez ama veri gerekir.
 */
/**
 * KIYAS ARAÇLARI (kullanıcı kararı 13.10.2026): merkez bunları kataloğa
 * kendisi ekler ve `reason = 'benchmark'` ile izler; uykuya alınmazlar.
 * Talep beklemezler — kullanıcı SAHİP OLMADIĞI şeyle kıyaslamak ister.
 * Fiyat hattı sıradan izlenen araçlarla aynı (Yahoo).
 *
 * Altın ons cinsinden (COMEX vadeli, USD). Gram altının TL getirisi ons
 * altının TL getirisiyle AYNIDIR (31,1035 katsayısı oranda sadeleşir), yani
 * Dönemsel Getiri'de rapor para birimi TRY seçilince gram altın kıyası olur.
 * TÜFE ve mevduat TCMB EVDS anahtarı gelince (`BENCHMARK_CODES`).
 */
export const KIYAS_ARACLARI = [
  {
    exchange: "BIST",
    symbol: "XU100",
    sourceSymbol: "XU100.IS",
    assetClass: "equity_tr",
    currency: "TRY",
    name: "BIST 100 endeksi",
  },
  {
    exchange: "COMEX",
    symbol: "GC=F",
    sourceSymbol: "GC=F",
    assetClass: "commodity",
    currency: "USD",
    name: "Altın (ons, COMEX vadeli)",
  },
] as const satisfies readonly {
  exchange: string;
  symbol: string;
  sourceSymbol: string;
  assetClass: AssetClass;
  currency: CurrencyCode;
  name: string;
}[];

export const BENCHMARK_CODES = ["BIST100", "XAUTRY", "USDTRY", "DEPOSIT_TR", "CPI_TR"] as const;
export type BenchmarkCode = (typeof BENCHMARK_CODES)[number];

/**
 * Kıyas serisinin izleme durumu yoktur (talep beklemez, hep tam tutulur),
 * o yüzden araç şeklindeki `Coverage` buraya uymuyordu — B şeridi
 * `instrumentId` alanına kod yazmak zorunda kalmıştı. Kendi kapsam tipi.
 */
export interface SeriesCoverage {
  from: IsoDate | null;
  to: IsoDate | null;
  gaps: readonly { from: IsoDate; to: IsoDate }[];
}

export interface BenchmarkResponse {
  series: readonly {
    code: string;
    name: string;
    currency: CurrencyCode | null;
    values: readonly { date: IsoDate; value: Decimal }[];
    coverage: SeriesCoverage;
  }[];
}

// ---------------------------------------------------------------------------
// Kuyruk — öncelik ve bütçe
// ---------------------------------------------------------------------------

/**
 * İLERİ-ÖNCE SIRALAMA: geçmişi baştan çekmeye kalkarsak kullanıcı hareketi
 * girdikten sonra dakikalarca boş ekrana bakar. Anlık fiyat + son 30 gün tek
 * çağrıyla saniyeler içinde gelir, panel çalışmaya başlar, on yıllık geçmiş
 * arkada dolar. Kullanıcının beklemesi `spot_ready`'de biter, `ready`'de değil.
 */
/**
 * ÖNCELİKLER BURADA DEĞİL: tek doğruluk kaynağı `@parafon/contracts`
 * içindeki `BACKFILL_PRIORITY`. Burada bir kopya vardı; aynı değerlerle
 * başlamıştı ama ayrışmaları hâlinde hata sessiz olurdu (B şeridi bildirdi).
 * Takma ad yaratmak yerine kopya SİLİNDİ — tüketiciler doğrudan oradan alır.
 */

/** Kabul kriteri: yeni araç eklendiğinde bu süre içinde `spot_ready` olmalı. */
export const SPOT_READY_TARGET_SECONDS = 10;

/**
 * TEMİZLEME SÜRESİ BURADA DEĞİL: tek kaynak `@parafon/contracts` içindeki
 * `PURGE_AFTER_DORMANT_DAYS` (365 gün, kullanıcı kararı).
 *
 * NOT: `purge` bir `TrackState` DEĞİL, bir eylemdir — satır silinir. B şeridi
 * bunu doğru uyguladı (`purgeDormant`, yalnızca `dormant` + 365 gün).
 */

export interface QueueStats {
  pending: Readonly<Record<"spot" | "recent" | "history", number>>;
  inFlight: number;
  /** Kaynak başına kalan hız bütçesi — paylaşılan kova. */
  budgetBySource: Readonly<Record<string, { remaining: number; resetsInMs: number }>>;
  oldestPendingAgeMs: number | null;
}

// ---------------------------------------------------------------------------
// /flags — kalite işaretlerinin okuma yüzeyi
// ---------------------------------------------------------------------------

/**
 * `data_quality_flags` dolduruluyor ve "eksiklik sessiz kalmayacak" kuralı
 * buna dayanıyor — ama okuma yüzeyi yoktu (B şeridi bildirdi). Analiz ve
 * arayüz şeritleri bunu okuyup raporda gösterecek.
 */
export interface FlagsRequest {
  instrumentIds?: readonly InstrumentId[];
  from?: IsoDate;
  to?: IsoDate;
  includeResolved?: boolean;
  limit?: number;
}

export interface FlagsResponse {
  flags: readonly {
    instrumentId: InstrumentId | null;
    date: IsoDate | null;
    issue: string;
    detail: string;
    resolved: boolean;
    createdAt: IsoTimestamp;
  }[];
  total: number;
}

// ---------------------------------------------------------------------------
// Bilinen ve KABUL EDİLMİŞ eksiklikler
// ---------------------------------------------------------------------------

/**
 * KAP kullanılamadığı için bedelsiz (bonus_issue) ve rüçhan (rights_issue)
 * ayrımı yapılamıyor; Yahoo yalnızca temettü ve bölünme veriyor. BIST'te
 * bedelsiz yaygın olduğu için bu getiri hesabında gerçek bir eksiklik.
 *
 * Kullanıcı bunu KABUL ETTİ (Aşama 0 kapısı). Kural: eksiklik sessiz
 * kalmayacak — etkilenen araçlar raporda işaretli görünecek.
 */
export const CORPORATE_ACTION_GAPS = {
  bonusIssueUnavailable: true,
  rightsIssueUnavailable: true,
  reason: "KAP belgelenmiş API sunmuyor; Yahoo events=div|split yalnızca temettü ve bölünme veriyor",
} as const;

/**
 * Yahoo tek ve resmî olmayan hisse kaynağımız; yedeği (Twelve Data)
 * ölçülemedi. Kullanıcı kararı: şimdilik Yahoo yeterli, risk yazılı duruyor.
 * Yahoo bozulursa BIST ve yabancı hisse fiyatları DURUR.
 */
export const EQUITY_SOURCE_HAS_NO_FALLBACK = true;

/**
 * Gram altın XAU/USD × USDTRY / 31.1035 ile türetilir, ama XAU/USD kaynağı
 * HÂLÂ DOĞRULANMADI. Aşama 2'nin ilk işlerinden biri.
 */
export const TROY_OUNCE_GRAMS = "31.1035" as const;
export const XAU_SOURCE_UNVERIFIED = true;

// ---------------------------------------------------------------------------
// YENİLEME TAKVİMİ — P-b (kullanıcı kararı K2, 25.09.2026)
// ---------------------------------------------------------------------------

/**
 * BİR KAYNAĞIN GÜNLÜK VERİSİNİ NE ZAMAN İSTEYECEĞİMİZ — YUVA LİSTESİ.
 *
 * Eskiden merkezde tek bir eşik vardı (`SOURCE_PUBLICATION`: TEFAS 11:00,
 * TCMB 15:30). Eşiğin tek gerekçesi bir KİLİTTİ: `recent` işinin anahtarı
 * tarih taşıyordu, erken sorulan gün boş yanıt alıp anahtarı kilitliyor ve o
 * günün fiyatı ancak ertesi gün geliyordu. Eşik bu yüzden GEÇ tarafa
 * konmuştu: erken yayını beklemek yalnızca gecikme, erken sormak kayıp.
 *
 * YUVA KİLİDİ KALDIRIR: `recent` işinin anahtarı artık geçen son yuvayı da
 * taşıyor (`…|yuva=10:00`). 08:30'da boş dönen gün 10:00'da, o da boşsa
 * 13:00'te yeniden istenir. Böylece erken sormak artık kayıp değil yalnızca
 * bir boş çağrı; yayın saatini TAHMİN etmek zorunda kalmıyoruz.
 *
 * NEDEN SÖZLEŞMEDE: cihaz da okuyacak (P-c — "fiyat kendi periyodundan
 * eskiyse bayat"). İki kopya ayrışırsa merkez 13:00'te yenilediği fiyatı
 * cihaz 11:00'den beri bayat sayar.
 *
 * HAFTA SONU YUVA YOK: TEFAS ve TCMB hafta sonu yayımlamıyor (canlıda
 * ölçüldü 26.09: `prices_daily`de cumartesi/pazar tarihli satır SIFIR).
 * Cumartesi günü "geçen son yuva" cuma 13:00'tür — yani yeni iş açılmaz.
 *
 * TATİL BİLİNMİYOR ve bu bilinçli: yuva tatilde de çalar, kaynak boş döner,
 * bedeli bir boş çağrı. Bayatlık tarafında tatilin karşılığı P-c'de.
 *
 * TABLODA OLMAYAN KAYNAK = TAKVİMSİZ: bugünün verisi her an istenebilir
 * (Yahoo, Binance gün içi fiyat veriyor). Gün içi takvim P-d'nin işi.
 */
export interface ScheduleSlot {
  /** Yerel saat, 0–23. */
  readonly hour: number;
  readonly minute: number;
}

export interface SourceSchedule {
  /** IANA dilim adı. Sabit kayma YAZILMIYOR: yaz saati kararı değişebilir. */
  readonly zone: string;
  /** ISO hafta günleri: 1 = pazartesi … 7 = pazar. */
  readonly weekdays: readonly number[];
  /** Gün içindeki yuvalar, artan sırada. */
  readonly slots: readonly ScheduleSlot[];
}

const IS_GUNLERI = [1, 2, 3, 4, 5] as const;

export const SOURCE_SCHEDULE: Readonly<Record<string, SourceSchedule>> = {
  /** Fon birim pay değeri sabah yayınlanıyor, "en geç 11:00" (kullanıcı). */
  tefas: {
    zone: "Europe/Istanbul",
    weekdays: IS_GUNLERI,
    slots: [
      { hour: 8, minute: 30 },
      { hour: 10, minute: 0 },
      { hour: 13, minute: 0 },
    ],
  },
  /**
   * TCMB gösterge kurları ~15:30. 16:00 YUVASI GEÇ TARAFTA DURMAK ZORUNDA:
   * kur eşitlemesinin su işareti (`fx_sync_state`) boş yanıtta da ilerliyor
   * (10.09.2026 canlı kusuru) — yani burada yuva kilidi kaldırmıyor, erken
   * sormak hâlâ o günü kaybetmek demek.
   */
  tcmb: { zone: "Europe/Istanbul", weekdays: IS_GUNLERI, slots: [{ hour: 16, minute: 0 }] },
  /**
   * ECB referans kurları ~16:00 CET; canlıda 16:50 Berlin turunda geldi.
   * Aynı su işareti kuralı: geç tarafta.
   */
  frankfurter: { zone: "Europe/Berlin", weekdays: IS_GUNLERI, slots: [{ hour: 17, minute: 0 }] },
};

/**
 * VARLIK SINIFI → KAYNAK — 00-mimari-ve-plan §"Veri kaynakları" tablosunun
 * kodlaşmış hâli. Merkezde doğdu (`central/src/domain/sources.ts`), P-c ile
 * SÖZLEŞMEYE taşındı: cihaz bir fiyatın bayat olup olmadığına karar vermek
 * için aracın HANGİ TAKVİME bağlı olduğunu bilmek zorunda. İki kopya
 * ayrışırsa merkez bir fonu TEFAS takvimiyle yenilerken cihaz onu takvimsiz
 * sayıp cumartesi günü cuma fiyatına yine sarı yakar.
 *
 * `equity_foreign`/`etf` de Yahoo'ya gidiyor: Twelve Data Aşama 0'da
 * ölçülemedi ve `EQUITY_SOURCE_HAS_NO_FALLBACK = true` damgası bunun kaydı.
 */
export const NO_SOURCE = "manual" as const;

export const SOURCE_FOR_ASSET_CLASS: Readonly<Record<AssetClass, string>> = {
  equity_tr: "yahoo",
  equity_foreign: "yahoo",
  etf: "yahoo",
  crypto: "binance",
  fund_tr: "tefas",
  commodity: "yahoo",
  fx: "frankfurter",
  deposit: NO_SOURCE,
  other: NO_SOURCE,
};

/**
 * KAYNAK ANLIK FİYAT SUNUYOR MU — merkezde doğdu (`domain/sources.ts`, gerekçe
 * orada), P-d ile SÖZLEŞMEYE taşındı: cihaz her eşitlemede `/spot` soruyor ve
 * yalnızca TEFAS fonundan oluşan bir portföyde bu soru boşuna bir çağrı.
 * Tabloda olmayan kaynak "sunuyor" sayılır (gürültülü taraf — gerekçe merkezde).
 */
export const SOURCE_PROVIDES_SPOT: Readonly<Record<string, boolean>> = {
  yahoo: true,
  binance: true,
  coingecko: true,
  frankfurter: true,
  tefas: false,
  tcmb: false,
  [NO_SOURCE]: false,
};

/**
 * HIZ (12.10.2026, günlük seri): biçimleyici dilim başına BİR KEZ kuruluyor
 * ve aynı (an, dilim) için sonuç hatırlanıyor. Bir yıllık seride aynı "şimdi"
 * binlerce kez çevriliyordu; her seferinde yeni bir `Intl.DateTimeFormat`
 * kurmak sürenin %20'siydi. Önbellek küçük ve sınırlı; sonuç aynı.
 */
const ZONE_FORMAT = new Map<string, Intl.DateTimeFormat>();
type ZonedParts = { readonly date: IsoDate; readonly minutes: number; readonly isoWeekday: number };
const ZONED_CACHE = new Map<string, ZonedParts>();
const ZONED_CACHE_MAX = 256;

/** Bir anın verilen dilimdeki takvim günü, gün içi dakikası ve ISO hafta günü. */
export function zonedParts(now: Date, zone: string): ZonedParts {
  const cacheKey = `${now.getTime()}|${zone}`;
  const hit = ZONED_CACHE.get(cacheKey);
  if (hit !== undefined) return hit;
  let format = ZONE_FORMAT.get(zone);
  if (format === undefined) {
    // "en-CA" YYYY-MM-DD üretir; `hourCycle: "h23"` gece yarısını 00 yapar
    // (`hour12: false` bazı ortamlarda "24" veriyor ve karşılaştırma ters döner).
    format = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    ZONE_FORMAT.set(zone, format);
  }
  const parts = format.formatToParts(now);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "";
  const date = `${get("year")}-${get("month")}-${get("day")}`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // ICU eksik bir derlemede dilim sessizce UTC'ye düşebilir; sessiz düşmek
    // yerine gürültü çıkarıyoruz.
    throw new Error(`saat dilimi çözülemedi: ${zone}`);
  }
  const result: ZonedParts = {
    date: date as IsoDate,
    minutes: Number(get("hour")) * 60 + Number(get("minute")),
    isoWeekday: isoWeekdayOf(date as IsoDate),
  };
  if (ZONED_CACHE.size >= ZONED_CACHE_MAX) ZONED_CACHE.clear();
  ZONED_CACHE.set(cacheKey, result);
  return result;
}

function isoWeekdayOf(day: IsoDate): number {
  const d = new Date(`${day}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

function gunEkle(day: IsoDate, n: number): IsoDate {
  const t = new Date(`${day}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10) as IsoDate;
}

function slotLabel(s: ScheduleSlot): string {
  return `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`;
}

export interface PassedSlot {
  /** Yuvanın KAYNAK DİLİMİNDEKİ takvim günü. */
  readonly date: IsoDate;
  /** "HH:MM" — iş anahtarına bu girer. */
  readonly slot: string;
}

/**
 * `now` anında ya da öncesinde çalmış EN SON yuva. Yuva anı dahil (13:00:00'de
 * 13:00 yuvası geçmiş sayılır). Hafta sonu ve yuva öncesi saatler bir önceki
 * iş gününün son yuvasına düşer.
 */
export function lastPassedSlot(schedule: SourceSchedule, now: Date): PassedSlot {
  if (schedule.weekdays.length === 0 || schedule.slots.length === 0) {
    throw new Error("takvimde yuva yok");
  }
  const local = zonedParts(now, schedule.zone);
  for (let k = 0; k <= 7; k += 1) {
    const day = gunEkle(local.date, -k);
    if (!schedule.weekdays.includes(isoWeekdayOf(day))) continue;
    for (let i = schedule.slots.length - 1; i >= 0; i -= 1) {
      const s = schedule.slots[i]!;
      if (k === 0 && s.hour * 60 + s.minute > local.minutes) continue;
      return { date: day, slot: slotLabel(s) };
    }
  }
  throw new Error("son yuva bulunamadı"); // weekdays boş değilse ulaşılamaz
}

/** Bir dilimdeki yerel duvar saatinin UTC anı (yaz saati geçişinde iki turla oturur). */
function zonedInstant(day: IsoDate, s: ScheduleSlot, zone: string): number {
  const wall = Date.parse(`${day}T${slotLabel(s)}:00Z`);
  let guess = wall;
  for (let tur = 0; tur < 2; tur += 1) {
    const p = zonedParts(new Date(guess), zone);
    const seen = Date.parse(`${p.date}T00:00:00Z`) + p.minutes * 60_000;
    guess += wall - seen;
  }
  return guess;
}

/** `now`dan SONRA çalacak ilk yuvanın anı. İşçi uykusunu buna göre kısaltır. */
export function nextSlotAt(schedule: SourceSchedule, now: Date): Date {
  if (schedule.weekdays.length === 0 || schedule.slots.length === 0) {
    throw new Error("takvimde yuva yok");
  }
  const local = zonedParts(now, schedule.zone);
  const nowMs = now.getTime();
  for (let k = 0; k <= 8; k += 1) {
    const day = gunEkle(local.date, k);
    if (!schedule.weekdays.includes(isoWeekdayOf(day))) continue;
    for (const s of schedule.slots) {
      const at = zonedInstant(day, s, schedule.zone);
      if (at > nowMs) return new Date(at);
    }
  }
  throw new Error("sonraki yuva bulunamadı");
}

/**
 * Bu takvimde verisi İSTENEBİLİR en son gün.
 *
 * = bugün bir yuva çaldıysa bugün; çalmadıysa bir sonraki yuvanın gününden
 * bir önceki gün, en fazla bugün. Hafta sonu
 * günleri böylece KAPSANIR: cumartesinin verisi hiç olmayacak, sormak
 * zararsız ve kur eşitlemesinin su işareti o günleri "soruldu" diye geçebilir
 * (eskiden de böyleydi). Kısıtlanan tek şey yuvası henüz çalmamış İŞ GÜNÜ.
 *
 * Örnek (TEFAS, 08:30 ilk yuva): pazartesi 08:00 → pazar; 08:30 → pazartesi;
 * cumartesi → cumartesi.
 */
export function publishedThroughDay(schedule: SourceSchedule, now: Date): IsoDate {
  const today = zonedParts(now, schedule.zone).date;
  // Bugün bir yuva çaldıysa bugün istenebilir.
  if (lastPassedSlot(schedule, now).date === today) return today;
  const next = zonedParts(nextSlotAt(schedule, now), schedule.zone).date;
  const before = gunEkle(next, -1);
  return before < today ? before : today;
}

/**
 * P-c · BAYATLIK İÇİN BEKLENEN SON GÜN.
 *
 * `askedDate` günü için bu takvimde ölçümü EN GEÇ hangi günün olması
 * beklenir: `askedDate`den (ve bugünden) geriye giderken, takvimin bir iş
 * günü olan VE o günün SON yuvası `now` anında çalmış olan ilk gün.
 *
 * NİÇİN SON YUVA, İLK DEĞİL: `publishedThroughDay` "istenebilir" sorusunu
 * yanıtlıyor ve ilk yuvaya bakıyor (08:30'da sormak zararsız, en kötü boş
 * döner). Bayatlık başka bir soru: "bu saatte o günün fiyatı ARTIK gelmiş
 * olmalı mıydı". TEFAS 08:30–13:00 arasında HERHANGİ bir anda yayımlayabilir;
 * pazartesi 09:00'da cuma fiyatı bayat DEĞİLDİR. Son yuva (13:00) geçtikten
 * sonra ise o günün fiyatı her yuvada sorulmuş olur — hâlâ yoksa bir şey
 * gerçekten eksiktir.
 *
 * HAFTA SONU: takvimin günü değil, atlanır. Cumartesi günü beklenen cuma.
 *
 * TATİL BİLİNMİYOR (kullanıcı kararı, 27.09.2026 — "tatilde de sarı"):
 * resmî tatilde fiyat gelmez ve bu fonksiyon o günü yine bekler, yani o gün
 * fiyat SARI görünür. Bilinçli: tatil takvimi olmadan "tatil" ile "kaynağın
 * tamamı durdu" (TEFAS kesintisi, işçi çöktü) ayırt edilemiyor ve yanlış
 * alarm, sessizce gizlenmiş bir bayatlıktan ucuzdur.
 */
export function expectedLatestDay(
  schedule: SourceSchedule,
  askedDate: IsoDate,
  now: Date,
): IsoDate {
  if (schedule.weekdays.length === 0 || schedule.slots.length === 0) {
    throw new Error("takvimde yuva yok");
  }
  const local = zonedParts(now, schedule.zone);
  const son = schedule.slots[schedule.slots.length - 1]!;
  const sonDakika = son.hour * 60 + son.minute;
  // Gelecekteki bir gün sorulsa bile beklenti bugünü geçemez.
  const start = askedDate < local.date ? askedDate : local.date;
  for (let k = 0; k <= 7; k += 1) {
    const day = gunEkle(start, -k);
    if (!schedule.weekdays.includes(isoWeekdayOf(day))) continue;
    if (day === local.date && local.minutes < sonDakika) continue;
    return day;
  }
  throw new Error("beklenen gün bulunamadı"); // weekdays boş değilse ulaşılamaz
}

// ---------------------------------------------------------------------------
// P-d · GÜN İÇİ FİYAT — PİYASA TAKVİMİ (kullanıcı kararı K2, 25.09.2026)
// ---------------------------------------------------------------------------

/**
 * GÜN İÇİ FİYATIN TAKVİMİ KAYNAĞA DEĞİL PİYASAYA BAĞLI.
 *
 * `SOURCE_SCHEDULE` kaynak adıyla anahtarlı ve TEFAS/TCMB için doğru: bir
 * kaynak, bir yayın saati. Yahoo ise İKİ piyasayı (BIST ve ABD) aynı
 * kaynaktan veriyor; seansları farklı dilimde ve farklı saatte. Yahoo'yu
 * `SOURCE_SCHEDULE`e koymak iki takvimden birini seçmek demekti.
 *
 * YAHOO `SOURCE_SCHEDULE`E BİLİNÇLİ OLARAK GİRMİYOR: o tablo `recent`
 * (günlük bar) işinin anahtarını da belirliyor. Günlük bar seans İÇİNDE
 * istenirse yarım bir gün "kapanış" diye yazılır. Bugünkü takvimsiz kural
 * (`recent` UTC gününe bir kez, gece yarısından sonraki ilk tur = BIST ve
 * ABD seansı kapalıyken) tam olarak doğru zamanı seçiyor; dokunulmuyor.
 * Gün içinde değişen şey yalnızca `spot` işi ve onun yuvası.
 *
 * YUVALAR: seans boyunca 30 dakikada bir + kapanıştan sonra BİR yuva daha
 * (kapanış fiyatını yakalamak için; Yahoo BIST'i ~15 dk gecikmeli veriyor).
 *  - BIST 10:00–18:10 İstanbul → 10:00, 10:30 … 18:00, 18:30
 *  - ABD  iş günü boyunca (New York) 00:00, 00:30 … 23:30. Normal seans
 *    09:30–16:00; öncesi, sonrası ve gece SEANS DIŞI (`usSeansDisiOf`,
 *    kullanıcı kararları 04.11 ve 05.11.2026): her yuvada sorulur, gelen
 *    seans dışı fiyat normal fiyattan AYRI saklanır. Gece seansı pazar
 *    20:00'de açılır; takvim iş gününe bağlı olduğundan pazar 20:00–24:00
 *    sorulmaz (pazartesi 00:00 yuvası ilk gece fiyatını alır), cuma gecesi
 *    işlem olmadığı için cuma 20:00 sonrası yuvalar fiyatı gece diye
 *    SINIFLAMAZ.
 * Dilim IANA adıyla: ABD'nin yaz saati geçişi (mart/kasım) İstanbul'la aynı
 * gün olmuyor, sabit kayma yazmak yılda iki kez bir saat kaydırırdı.
 *
 * TATİL BİLİNMİYOR (P-c ile aynı karar): yuva tatilde de çalar, Yahoo son
 * kapanışı döndürür, bayatlık "tatilde sarı" kuralına uyar.
 */
export type MarketKey = "BIST" | "US";

function everyHalfHour(
  open: ScheduleSlot,
  lastInSession: ScheduleSlot,
  afterClose: ScheduleSlot,
): readonly ScheduleSlot[] {
  const out: ScheduleSlot[] = [];
  const end = lastInSession.hour * 60 + lastInSession.minute;
  for (let m = open.hour * 60 + open.minute; m <= end; m += 30) {
    out.push({ hour: Math.floor(m / 60), minute: m % 60 });
  }
  out.push(afterClose);
  return out;
}

export const MARKET_SCHEDULE: Readonly<Record<MarketKey, SourceSchedule>> = {
  BIST: {
    zone: "Europe/Istanbul",
    weekdays: IS_GUNLERI,
    slots: everyHalfHour({ hour: 10, minute: 0 }, { hour: 18, minute: 0 }, { hour: 18, minute: 30 }),
  },
  US: {
    zone: "America/New_York",
    weekdays: IS_GUNLERI,
    slots: everyHalfHour({ hour: 0, minute: 0 }, { hour: 23, minute: 0 }, { hour: 23, minute: 30 }),
  },
};

/**
 * SEANS DIŞI FİYAT (kullanıcı kararları 04.11 ve 05.11.2026). ABD'de normal
 * seans 09:30–16:00 New York; 04:00–09:30 seans öncesi, 16:00–20:00 seans
 * sonrası, 20:00–04:00 gece seansı (pazar 20:00 → cuma 04:00). Bir anın
 * hangisine düştüğü yalnızca saatle, IANA dilimiyle bulunur (yaz saati
 * geçişi). Normal seans, cuma 20:00 sonrası ve hafta sonu → `null`.
 *
 * Gece fiyatının İŞLEM saati kaynakta yok (Yahoo `meta.fulldayPrice`,
 * saatsiz); saklanan an merkezin sorduğu andır ve ekranda "… itibarıyla"
 * diye söylenir — işlem saati gibi sunulmaz.
 */
export type SeansDisi = "pre" | "post" | "night";

export const US_SEANS_DISI: Readonly<Record<"pre" | "post", { from: ScheduleSlot; to: ScheduleSlot }>> = {
  pre: { from: { hour: 4, minute: 0 }, to: { hour: 9, minute: 30 } },
  post: { from: { hour: 16, minute: 0 }, to: { hour: 20, minute: 0 } },
};

export function usSeansDisiOf(ts: IsoTimestamp | Date): SeansDisi | null {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const p = zonedParts(d, MARKET_SCHEDULE.US.zone);
  const m = p.minutes;
  const wd = p.isoWeekday; // 1 pazartesi … 7 pazar
  // Gece: pazar–perşembe 20:00 sonrası, pazartesi–cuma 04:00 öncesi.
  if ((m >= 20 * 60 && (wd === 7 || wd <= 4)) || (m < 4 * 60 && wd <= 5)) return "night";
  if (!MARKET_SCHEDULE.US.weekdays.includes(wd)) return null;
  for (const k of ["pre", "post"] as const) {
    const w = US_SEANS_DISI[k];
    if (m >= w.from.hour * 60 + w.from.minute && m < w.to.hour * 60 + w.to.minute) return k;
  }
  return null;
}

/**
 * Yahoo'nun ABD borsa kodları (chart `meta.exchangeName` / arama `exchange`).
 * Listede olmayan borsa (LSE, XETRA, OTC…) TAKVİMSİZ kalır: seans saatini
 * bilmediğimiz bir piyasaya uydurma bir takvim yazmak, bayatlık hükmünü
 * olmayan bir olguya dayandırmak olurdu. OTC (`PNK`) bilinçli olarak dışarıda:
 * işlem seyrek, 30 dakikalık beklenti orada bir anormallik ölçüsü değil.
 */
export const US_EXCHANGES: readonly string[] = [
  "NMS", "NGM", "NCM", "NAS", "NASDAQ", "NYQ", "NYS", "NYSE", "PCX", "ARCA", "NYSEARCA",
  "ASE", "AMEX", "BTS", "BATS", "CBOE",
];

/**
 * Bir aracın gün içi takvimi hangi piyasanın. YALNIZCA Yahoo araçları için
 * anlamlı; başka kaynakta (Binance 7/24, TEFAS günlük) `null`.
 *
 * `equity_tr` daima BIST. Diğerleri borsa koduna bakar: BIST'te işlem gören
 * ETF (`exchange = "BIST"`) BIST takvimini alır. Borsa bilinmiyorsa `null` —
 * künyesi henüz gelmemiş araç eski (takvimsiz) kuralda kalır.
 */
export function marketOf(
  assetClass: AssetClass | string | null,
  exchange: string | null,
): MarketKey | null {
  if (assetClass === null) return null;
  const source = (SOURCE_FOR_ASSET_CLASS as Readonly<Record<string, string>>)[assetClass];
  if (source !== "yahoo") return null;
  if (assetClass === "equity_tr") return "BIST";
  if (exchange === null) return null;
  const ex = exchange.trim().toUpperCase();
  if (ex === "BIST" || ex === "IST") return "BIST";
  return US_EXCHANGES.includes(ex) ? "US" : null;
}

/** Geçmiş bir yuvanın UTC anı. */
export function passedSlotInstant(schedule: SourceSchedule, passed: PassedSlot): Date {
  const [h, m] = passed.slot.split(":").map((x) => Number(x));
  return new Date(zonedInstant(passed.date, { hour: h ?? 0, minute: m ?? 0 }, schedule.zone));
}

/**
 * GÖZLEM GECİKMESİ: yuva çaldıktan sonra işçinin işi alıp Yahoo'ya sorması ve
 * cihazın merkezden okuması için tanınan süre. 15 dakika: işçi turu
 * yuvada uyanıyor (`nextDueMs`), kuyruk Yahoo'da dakikada 60 jetonla akıyor —
 * 200 araçta bile yuva sonrası birkaç dakika. Bu süre içinde bir önceki
 * yuvanın gözlemi hâlâ taze sayılır.
 */
export const SPOT_OBSERVATION_LAG_MINUTES = 15;

/**
 * P-d · GÜN İÇİ BAYATLIK — "bu anda merkez bu fiyatı EN GEÇ ne zaman
 * GÖZLEMİŞ olmalıydı".
 *
 * NİÇİN GÖZLEM ANI, FİYATIN KENDİ SAATİ DEĞİL: Yahoo'nun `regularMarketTime`ı
 * SON İŞLEMİN saati. Seyrek işlem gören bir BIST hissesinde son işlem 40
 * dakika önce olabilir — bu bir anormallik DEĞİL, piyasanın kendisi. Seans
 * kapandıktan sonra da saat kapanışta donar; gece 23:00'te 18:10 fiyatı
 * alınabilecek en taze fiyattır. "Fiyatın saati yuvadan eski mi" diye
 * sormak bu iki durumda da sarı yakardı (§4e ihlali). Doğru soru: "merkez
 * son yuvada SORDU mu" — sorduysa elindeki en taze cevap budur.
 *
 * Fiyatın kendi saati yine de GÖSTERİLİR (K3: saat damgalı); hüküm gözleme
 * dayanır.
 */
export function expectedObservedAfter(schedule: SourceSchedule, now: Date): Date {
  const shifted = new Date(now.getTime() - SPOT_OBSERVATION_LAG_MINUTES * 60_000);
  return passedSlotInstant(schedule, lastPassedSlot(schedule, shifted));
}

// ---------------------------------------------------------------------------
// /spot — P-d: son anlık fiyat, GÖZLEM ANIYLA
// ---------------------------------------------------------------------------

export interface SpotRequest {
  ids: readonly InstrumentId[];
}

/**
 * Araç başına SON anlık fiyat. Barlardan AYRI bir uç nokta, çünkü cihazın
 * bar eşitlemesi artımlı (yalnızca eksik GÜNLERİ istiyor): bugünün barı
 * elde olunca `/prices` hiç çağrılmıyor, gün içi fiyat da hiç gelmezdi.
 *
 * Künye alanları (`assetClass`, `exchange`) cihaz takvimi bulabilsin diye
 * taşınıyor: "Clear site data"dan sonra `.pfai`den dönen bir defterde
 * `instrument_cache` künyesiz kalıyordu (01.10 öncesi); bu yanıt onu da
 * dolduruyor.
 */
export interface SpotResponse {
  quotes: readonly {
    instrumentId: InstrumentId;
    assetClass: AssetClass | null;
    exchange: string | null;
    /** `marketOf` — merkezin hesabı; cihaz aynı fonksiyonla aynısını bulur. */
    market: MarketKey | null;
    /** Son fiyat; hiç yoksa `null` (kaynak anlık fiyat sunmuyor ya da henüz gelmedi). */
    quote: {
      /** Fiyatın KENDİ anı (Yahoo: son işlemin saati). */
      ts: IsoTimestamp;
      price: Decimal;
      currency: CurrencyCode;
      source: string;
      /** Merkezin bu fiyatı kaynaktan EN SON ne zaman okuduğu — hüküm buna dayanır. */
      observedAt: IsoTimestamp;
    } | null;
    /**
     * SEANS DIŞI FİYAT (04.11.2026): normal seans fiyatından (`quote`) DAHA
     * YENİ son seans öncesi/sonrası fiyat; yoksa alan yok ya da `null`.
     * `quote`un yerine geçmez — hangisinin kullanılacağı cihazdaki ayar.
     */
    seansDisi?: {
      ts: IsoTimestamp;
      price: Decimal;
      currency: CurrencyCode;
      source: string;
      seans: SeansDisi;
      observedAt: IsoTimestamp;
    } | null;
  }[];
}

// ---------------------------------------------------------------------------
// /corporate-actions — H2: şirket olayları
// ---------------------------------------------------------------------------

/**
 * Hangi kaynak şirket olayı (temettü, bölünme) sunuyor — YAPISAL olgu.
 * Yahoo `events=div|split` veriyor (bkz. `CORPORATE_ACTION_GAPS`); TEFAS,
 * TCMB, Binance vermiyor. Olay sunmayan kaynakta "olay yok" bir ölçüm DEĞİL:
 * cihaz "kaydedilmemiş olay yok" diyemez, yalnızca "bilinmiyor".
 */
export const SOURCE_PROVIDES_ACTIONS: Readonly<Record<string, boolean>> = {
  yahoo: true,
};

export interface CorporateActionsRequest {
  ids: readonly InstrumentId[];
  from: IsoDate;
  to: IsoDate;
}

export interface CorporateActionDto {
  exDate: IsoDate;
  type: "dividend" | "split" | "bonus_issue" | "rights_issue" | "merger" | "delisting";
  ratioNumerator?: Decimal;
  ratioDenominator?: Decimal;
  amount?: Decimal;
  currency?: CurrencyCode;
  source: string;
}

/**
 * KAPSAM BURADA DA ŞART (`/prices`taki dürüstlük kuralı): olaylar barlarla
 * AYNI çağrıdan geliyor (`dailyWithActions`), yani olayların bilindiği dönem
 * barların bilindiği dönemdir. Kapsam dışındaki bir dönemde boş liste "olay
 * yok" DEMEK DEĞİL; cihaz orada damga basmaz.
 */
export interface CorporateActionsResponse {
  series: readonly {
    instrumentId: InstrumentId;
    /** Aracın kaynağı olay sunuyor mu (`SOURCE_PROVIDES_ACTIONS`). */
    provided: boolean;
    /** Olayların bilindiği zarf (o kaynağın barları); `null` = hiç yok. */
    coverage: { from: IsoDate; to: IsoDate } | null;
    actions: readonly CorporateActionDto[];
  }[];
}

// ---------------------------------------------------------------------------
// KASA YEDEĞİ — "Parafon sunucusu" hedefi (kullanıcı kararları 16–17.10.2026;
// `claude/notlar/2026-10-17-sunucu-yedegi-kararlar.md`).
//
// Hesap YOK. Cihaz 128 bitlik bir KASA KODU üretir; sunucu kodu saklamaz,
// klasörün adı kodun SHA-256 özetidir. Kod URL'ye yazılmaz (sunucu
// günlüğüne düşmesin): her istek POST, kod gövdede. Dosyalar sıradan `.pfai`
// metni — sunucu açamaz. Klasörde tutulan: dosya adı, içerik, boyut, değişme
// anı ve klasörün son açılma GÜNÜ (7 ay kuralı). Kimlik, adres, cihaz,
// profil adı YOK.
// ---------------------------------------------------------------------------

/** Kod: 26 karakter Crockford base32 (128 bit + 2 bit dolgu), tireyle 5'li gruplar hâlinde gösterilir. */
export const KASA_KODU_UZUNLUGU = 26;

/** Kodun kanonik hâli: yalnızca bu alfabe, büyük harf, tiresiz. */
export const KASA_KODU_ALFABESI = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** Hiçbir cihazdan bu kadar ay açılmayan klasör kesin silinir. */
export const KASA_SAKLAMA_AY = 7;

/** Bu hedefte kasa parolasının en az uzunluğu (ŞART; diğer hedeflerde uyarı). */
export const KASA_SUNUCU_PAROLA_EN_AZ = 12;

/** Sunucu sınırları — cihaz da aynı sayılarla önceden uyarır. */
export const KASA_SINIRLARI = {
  /** Tek dosya, bayt (UTF-8 metin). */
  dosyaBayt: 8 * 1024 * 1024,
  /** Klasördeki dosyaların toplamı, bayt. */
  klasorBayt: 128 * 1024 * 1024,
  /** Klasördeki dosya sayısı. */
  klasorDosya: 256,
  /** Dosya adı uzunluğu ve deseni. */
  adUzunlugu: 128,
} as const;

/** Dosya adı yalnızca bu desende: `.pfai` uzantılı, yol ayracı yok. */
export const KASA_DOSYA_ADI_DESENI = /^[A-Za-z0-9][A-Za-z0-9._-]{0,122}\.pfai$/;

/** `POST /kasa/ac` — yeni klasör. 201; kod zaten varsa 409 `KASA_VAR`. */
export interface KasaAcRequest {
  readonly kod: string;
}

/** `POST /kasa/liste` — 404 `KASA_YOK` (başarısız kod sorgusu sayılır). */
export interface KasaListeRequest {
  readonly kod: string;
}

export interface KasaDosyasiDto {
  readonly ad: string;
  readonly boyut: number;
  /** ISO zaman damgası (sunucunun yazma anı). */
  readonly degisti: string;
}

export interface KasaListeResponse {
  readonly dosyalar: readonly KasaDosyasiDto[];
}

/** `POST /kasa/oku` — 404 `KASA_YOK` ya da `DOSYA_YOK`. */
export interface KasaOkuRequest {
  readonly kod: string;
  readonly ad: string;
}

export interface KasaOkuResponse {
  readonly icerik: string;
}

/**
 * `POST /kasa/yaz` — üstüne yazar. 413 `DOSYA_BUYUK` / `KLASOR_DOLU`.
 */
export interface KasaYazRequest {
  readonly kod: string;
  readonly ad: string;
  readonly icerik: string;
}

/** `POST /kasa/sil` — dosya yoksa da 200 (silme kendini tekrarlayabilir). */
export interface KasaSilRequest {
  readonly kod: string;
  readonly ad: string;
}

/** `POST /kasa/kapat` — klasörü bütünüyle siler (kod yenileme). */
export interface KasaKapatRequest {
  readonly kod: string;
}

/**
 * Hata kodları. `KASA_YOK` = kod tanınmadı (kod yenilenmiş ya da klasör 7 ay
 * kuralıyla silinmiş olabilir — sunucu hangisi olduğunu bilmez).
 * `KASA_TAVAN` = sunucunun toplam alanı doldu, yeni klasör açılmıyor.
 * `RATE_LIMITED` = adres başına sınır (yeni klasör ya da başarısız kod).
 * `ABONELIK` = yeni klasör açmak ya da yazmak "Parafon sunucusuna yedek"
 * kabiliyeti ister (gelir modeli; jeton `X-Parafon-Jeton` başlığında). Okuma,
 * listeleme, silme ve kapatma serbest: geri yükleme hiçbir zaman kilitli
 * değil.
 */
export type KasaHataKodu =
  | "KASA_YOK"
  | "KASA_VAR"
  | "DOSYA_YOK"
  | "DOSYA_BUYUK"
  | "KLASOR_DOLU"
  | "KASA_TAVAN"
  | "GECERSIZ_KOD"
  | "GECERSIZ_AD"
  | "RATE_LIMITED"
  | "ABONELIK";

// ---------------------------------------------------------------------------
// GERİ BİLDİRİM — içe aktarılamayan satırlar ve ekstre biçimi (kullanıcı
// kararı 25.10.2026). Kullanıcı gönderilecek metni birebir görür ve açıkça
// onaylar; metin MASKELİDİR (rakam → 9, sözlükte olmayan harf → X/x). Sunucu
// maskeyi yeniden denetler: 0–8 rakamı taşıyan metni reddeder. Tutulan
// yalnızca kod, geldiği GÜN, kaynak ve metin; kimlik, adres, cihaz YOK.
// İşlenen kayıt silinir; işlenmeyen `saklamaGun` sonra kendiliğinden silinir.
// ---------------------------------------------------------------------------

export const GERI_BILDIRIM_KAYNAKLARI = ["ice-aktarma", "ekstre-bicimi"] as const;
export type GeriBildirimKaynagi = (typeof GERI_BILDIRIM_KAYNAKLARI)[number];

export const GERI_BILDIRIM_SINIRLARI = {
  /** Metin, UTF-8 bayt. Sunucunun genel gövde sınırının (256 KiB) altında kalır. */
  metinBayt: 200 * 1024,
  /** İşlenmeyen kayıt bu kadar gün sonra silinir. */
  saklamaGun: 90,
  /** Bekleyen kayıt sayısı bu sınıra ulaşınca yenisi alınmaz. */
  bekleyenEnCok: 500,
  /** Bir adresten saatte en çok kaç gönderim. */
  saatteEnCok: 10,
} as const;

/** Başvuru kodu: 8 karakter, `KASA_KODU_ALFABESI`nden; "XXXX-XXXX" gösterilir. */
export const GERI_BILDIRIM_KODU_UZUNLUGU = 8;

/** Ekstre biçimi dosyasının (`parafon-ekstre-bicimi`) tanıtıcısı. */
export const EKSTRE_BICIMI_ADI = "parafon-ekstre-bicimi";

/** `POST /geri-bildirim` — 201 `{ kod }`. */
export interface GeriBildirimRequest {
  readonly kaynak: GeriBildirimKaynagi;
  readonly metin: string;
}

export interface GeriBildirimResponse {
  readonly kod: string;
}

/** `GECERSIZ` = maske ya da biçim tutmadı; `DOLU` = bekleyen sınırı. */
export type GeriBildirimHataKodu = "GECERSIZ" | "BUYUK" | "DOLU" | "RATE_LIMITED";

const MASKESIZ_RAKAM = /[0-8٠-٨۰-۸०-८０-８]/u;

function utf8Bayt(s: string): number {
  return new TextEncoder().encode(s).length;
}

/**
 * Cihaz göndermeden önce, sunucu almadan önce AYNI denetim. `null` = uygun;
 * değilse sebep. Ekstre biçiminde konumlar (punto) rakamdır: yalnızca metin
 * alanları (`gorunum` satırları ve her parçanın 5. öğesi) denetlenir.
 */
export function geriBildirimDenetle(kaynak: string, metin: unknown): string | null {
  if (!(GERI_BILDIRIM_KAYNAKLARI as readonly string[]).includes(kaynak)) return "tanınmayan kaynak";
  if (typeof metin !== "string" || metin.trim() === "") return "metin boş";
  if (utf8Bayt(metin) > GERI_BILDIRIM_SINIRLARI.metinBayt) return "metin çok büyük";
  if (kaynak === "ice-aktarma") {
    return MASKESIZ_RAKAM.test(metin) ? "metin maskeli değil (rakam var)" : null;
  }
  let govde: unknown;
  try {
    govde = JSON.parse(metin);
  } catch {
    return "ekstre biçimi JSON değil";
  }
  const g = govde as { bicim?: unknown; sayfalar?: unknown };
  if (g === null || typeof g !== "object" || g.bicim !== EKSTRE_BICIMI_ADI || !Array.isArray(g.sayfalar)) {
    return "ekstre biçimi tanınmadı";
  }
  for (const s of g.sayfalar as unknown[]) {
    const sayfa = s as { gorunum?: unknown; parcalar?: unknown };
    if (sayfa === null || typeof sayfa !== "object" || !Array.isArray(sayfa.gorunum) || !Array.isArray(sayfa.parcalar)) {
      return "ekstre biçimi tanınmadı";
    }
    for (const satir of sayfa.gorunum as unknown[]) {
      if (typeof satir !== "string") return "ekstre biçimi tanınmadı";
      if (MASKESIZ_RAKAM.test(satir)) return "metin maskeli değil (rakam var)";
    }
    for (const p of sayfa.parcalar as unknown[]) {
      if (!Array.isArray(p) || p.length !== 5 || typeof p[4] !== "string") return "ekstre biçimi tanınmadı";
      if (p.slice(0, 4).some((x) => typeof x !== "number")) return "ekstre biçimi tanınmadı";
      if (MASKESIZ_RAKAM.test(p[4])) return "metin maskeli değil (rakam var)";
    }
  }
  return null;
}
